import React, {MouseEventHandler, useEffect, useState} from 'react';
import {Button, Col, DatePicker, Divider, Form, FormInstance, Input, Radio, Row, Select, Space} from "antd";
import {FormattedMessage} from "react-intl";
import {CaretDownOutlined, DeleteOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import {Procedure} from "../../../api-client";
import {ProcedureObject} from "./ProcedureObject";
import {calculateTotalPrice} from "../../../utils/medicalUtils";

interface ProcedureItemProps {
    form: FormInstance;
    procedure: ProcedureObject;
    procedures: Procedure[];
    onDelete: MouseEventHandler;
}

const ProcedureItem = ({form, procedure, procedures, onDelete}: ProcedureItemProps) => {
    const {Option} = Select;

    const [isPriceCalculatedInTreatment, setIsPriceCalculatedInTreatment] = useState<boolean | undefined>(procedure.price === 0);

    useEffect(() => {
        calculateTotalPrice(procedure.price || 0, procedure.discount || 0, procedure.laboratoryPrice || 0,
            (calculation: number) =>
                form.setFieldValue("procedureTotalPrice-" + procedure.uid, calculation.toFixed(2)));
    }, []);

    return (
        <>
            <Row gutter={24}>
                <Col md={17}>
                    <Form.Item name={"procedureName-" + procedure.uid}
                               className={"dentobook-form-item input-full-width"}
                               label={<FormattedMessage id="procedure"/>}
                               initialValue={procedure.procedureId}
                               rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}>
                        <Select
                            size={"large"}
                            className="dentobook-input"
                            suffixIcon={<CaretDownOutlined/>}
                            value={procedure.procedureId}
                            onChange={e => {
                                procedure.procedureId = e;

                                const medicalProcedure = procedures.filter(procedureItem => procedureItem.id === e)[0];
                                procedure.isPriceCalculatedInTreatment = medicalProcedure.defaultPrice === 0;
                                setIsPriceCalculatedInTreatment(medicalProcedure.defaultPrice === 0);

                                form.setFieldValue('procedurePrice-' + procedure.uid, medicalProcedure.defaultPrice || 0);
                                procedure.price = medicalProcedure.defaultPrice || 0;
                            }}
                            allowClear
                        >
                            {procedures.sort((procedureOne, procedureTwo) => procedureOne.name?.localeCompare(procedureTwo.name!)!)
                                .map(procedure => (
                                <Option key={procedure.id}
                                        value={procedure.id}>{procedure.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col md={7}>
                    <Form.Item name={"procedureStatus-" + procedure.uid}
                               className={"dentobook-form-item input-full-width"}
                               label={<FormattedMessage id="status"/>}
                               initialValue={procedure.status}
                               rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}>
                        <Select
                            size={"large"}
                            className="dentobook-input"
                            suffixIcon={<CaretDownOutlined/>}
                            value={procedure.status}
                            onChange={e => procedure.status = e}
                            allowClear
                        >
                            <Option value="IN_PROGRESS"><FormattedMessage id="in_progress"/></Option>
                            <Option value="DONE"><FormattedMessage id="done"/></Option>
                            <Option value="OFFERED"><FormattedMessage id="offered"/></Option>
                            <Option value="CANCELED"><FormattedMessage id="canceled"/></Option>
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={24}>
                <Col md={24}>
                    <Form.Item
                        name={"procedureDate-" + procedure.uid}
                        className={"dentobook-form-item input-full-width"}
                        initialValue={dayjs(procedure.date)}
                        label={<FormattedMessage id="date"/>}
                        hidden={true}
                        rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}>
                        <DatePicker
                            defaultPickerValue={dayjs(procedure.date)}
                            size={"large"}
                            bordered={true}
                            className="input-ninety-nine-percent"
                            format={"DD.MM.YYYY"}
                            onChange={e => {
                                if (e === null) {
                                    procedure.date = undefined;
                                } else {
                                    procedure.date = e.toString();
                                }
                            }} placeholder=""/>
                    </Form.Item>
                </Col>
            </Row>
            <Radio.Group value={!!isPriceCalculatedInTreatment}
                         onChange={e => {
                             procedure.isPriceCalculatedInTreatment = e.target.value;
                             setIsPriceCalculatedInTreatment(e.target.value);

                             if (e.target.value) {
                                 procedure.price = 0;
                                 procedure.discount = 0;
                                 procedure.laboratoryPrice = 0;
                                 form.setFieldsValue({
                                     ['procedurePrice-' + procedure.uid]: 0,
                                     ['procedureDiscount-' + procedure.uid]: 0,
                                     ['procedureLaboratoryPrice-' + procedure.uid]: 0,
                                     ['procedureTotalPrice-' + procedure.uid]: 0,
                                 });
                             }
                         }}
                         className="mb-24"
            >
                <Space direction="vertical">
                    <Radio value={true}><FormattedMessage id="procedure_calculated_in_treatment"/></Radio>
                    <Radio value={false}><FormattedMessage id="procedure_not_calculated_in_treatment"/></Radio>
                </Space>
            </Radio.Group>
            {!isPriceCalculatedInTreatment ?
                <>
                    <Row gutter={24}>
                        <Col md={8}>
                            <Form.Item
                                name={"procedurePrice-" + procedure.uid}
                                label={<FormattedMessage id="price"/>}
                                className={"dentobook-form-item dentobook-simple-input"}
                                initialValue={procedure.price}
                                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                            >
                                <Input
                                    bordered={false}
                                    className="input-ninety-percents dentobook-inputs"
                                    value={procedure.price ?? undefined}
                                    onChange={e => {
                                        procedure.price = +e.target.value;

                                        calculateTotalPrice(+e.target.value, procedure.discount || 0, procedure.laboratoryPrice || 0,
                                            (calculation: number) =>
                                                form.setFieldValue("procedureTotalPrice-" + procedure.uid, calculation.toFixed(2)));
                                    }}/>
                            </Form.Item>
                        </Col>
                        <Col md={8}>
                            <Form.Item
                                name={"procedureDiscount-" + procedure.uid}
                                label={<FormattedMessage id="discount"/>}
                                className={"dentobook-form-item dentobook-simple-input"}
                                initialValue={procedure.discount || 0}
                                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                            >
                                <Input
                                    bordered={false}
                                    className="input-ninety-percents dentobook-inputs"
                                    value={procedure.discount ?? undefined}
                                    onChange={e => {
                                        procedure.discount = +e.target.value;

                                        calculateTotalPrice(procedure.price || 0, +e.target.value, procedure.laboratoryPrice || 0,
                                            (calculation: number) =>
                                                form.setFieldValue("procedureTotalPrice-" + procedure.uid, calculation.toFixed(2)));
                                    }}/>
                            </Form.Item>
                        </Col>
                        <Col md={8}>
                            <Form.Item
                                name={"procedureLaboratoryPrice-" + procedure.uid}
                                label={<FormattedMessage id="laboratory_price"/>}
                                className={"dentobook-form-item dentobook-simple-input"}
                                initialValue={procedure.laboratoryPrice}
                                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                            >
                                <Input
                                    bordered={false}
                                    className="input-ninety-percents dentobook-inputs"
                                    value={procedure.laboratoryPrice ?? undefined}
                                    onChange={e => {
                                        procedure.laboratoryPrice = +e.target.value;

                                        calculateTotalPrice(procedure.price || 0, procedure.discount || 0, +e.target.value,
                                            (calculation: number) =>
                                                form.setFieldValue("procedureTotalPrice-" + procedure.uid, calculation.toFixed(2)));
                                    }}/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col md={12}>
                            <Form.Item
                                label={<FormattedMessage id="total"/>}
                                className={"dentobook-form-item dentobook-simple-input"}
                                name={"procedureTotalPrice-" + procedure.uid}
                                initialValue={'0.00'}
                            >
                                <Input
                                    disabled={true}
                                    bordered={false}
                                    className="input-ninety-percents dentobook-inputs"/>
                            </Form.Item>
                        </Col>
                        <Col md={12}>
                            <Form.Item
                                className={"dentobook-form-item dentobook-simple-input mt-32"}
                                name={"procedurePaidAt-" + procedure.uid}
                                initialValue={procedure.paidAt ? '1' : '0'}
                                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                            >
                                <Select
                                    size={"large"}
                                    className="dentobook-input"
                                    suffixIcon={<CaretDownOutlined/>}
                                    value={procedure.paidAt ? '1' : '0'}
                                    onChange={e => {
                                        if (+e) {
                                            procedure.paidAt = (new Date()).toISOString();
                                        } else {
                                            procedure.paidAt = undefined;
                                        }
                                    }}
                                >
                                    <Select.Option value="1"><FormattedMessage id="paidFinance"/></Select.Option>
                                    <Select.Option value="0"><FormattedMessage id="unpaidFinance"/></Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </>
                : null}
            <Button shape="round"
                    className={"dentobook-btn-medium color-error float-right mb-32 " + (isPriceCalculatedInTreatment ? "mt-5" : "mt-72")}
                    type="default"
                    onClick={onDelete}>
                <DeleteOutlined/>
                {' '}
                <FormattedMessage
                    id="delete_text"/></Button>
            <Divider/>
        </>
    );
};

export default ProcedureItem;